import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import ThriveModal from "../../common/ThriveModal";
import { axiosInstance } from "../../../utils/networking";
import {
  SbcStatsMetrics,
  SbcStatsResponse,
  SbcStatsUser,
} from "../../../interfaces";
dayjs.extend(quarterOfYear);

const MAGENTA = "#9E1F63FF";
const MAGENTA_TINT = "#F9E6F0";

type Preset = "ytd" | "this_quarter" | "last_quarter" | "all" | "custom";

interface SbcStatsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  /** The filtered userStats rows (userId, firstName, lastName, email). */
  users: any[];
  onOpenDetail: (user: any) => void;
}

type Range = { start: Dayjs | null; end: Dayjs | null };

const presetRange = (preset: Exclude<Preset, "custom">): Range => {
  const today = dayjs();
  switch (preset) {
    case "ytd":
      return { start: today.startOf("year"), end: today };
    case "this_quarter":
      return { start: today.startOf("quarter"), end: today };
    case "last_quarter": {
      const lastQ = today.subtract(1, "quarter");
      return { start: lastQ.startOf("quarter"), end: lastQ.endOf("quarter") };
    }
    case "all":
    default:
      return { start: null, end: null };
  }
};

const sameDay = (a: Dayjs | null, b: Dayjs | null) =>
  a === null || b === null ? a === b : a.isSame(b, "day");

const detectPreset = (range: Range): Preset => {
  const presets: Exclude<Preset, "custom">[] = [
    "ytd",
    "this_quarter",
    "last_quarter",
    "all",
  ];
  for (const p of presets) {
    const r = presetRange(p);
    if (sameDay(r.start, range.start) && sameDay(r.end, range.end)) return p;
  }
  return "custom";
};

type MetricKey = Exclude<keyof SbcStatsMetrics, "referralTypes">;
type RowDef =
  | { kind: "metric"; label: string; key: MetricKey; hours?: boolean }
  | { kind: "referralType"; label: string; key: string };
type SectionDef = { title: string; rows: RowDef[] };

const REFERRAL_TYPE_ORDER = [
  "Legal",
  "Accounting",
  "Marketing",
  "HR",
  "IT",
  "Business Consultant",
];

const valueOf = (m: SbcStatsMetrics | undefined, row: RowDef): number => {
  if (!m) return 0;
  const v =
    row.kind === "metric" ? m[row.key] : (m.referralTypes || {})[row.key];
  return typeof v === "number" && !Number.isNaN(v) ? v : 0;
};

const formatValue = (v: number, hours?: boolean) =>
  hours
    ? v.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    : v.toLocaleString();

const SbcStatsModal = ({
  open,
  setOpen,
  startDate,
  endDate,
  users,
  onOpenDetail,
}: SbcStatsModalProps) => {
  const [range, setRange] = useState<Range>({ start: startDate, end: endDate });
  const [preset, setPreset] = useState<Preset>(
    detectPreset({ start: startDate, end: endDate }),
  );
  const [data, setData] = useState<SbcStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadSeconds, setLoadSeconds] = useState<number | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const requestId = useRef(0);

  // Re-seed from the page's date filter each time the modal opens.
  useEffect(() => {
    if (open) {
      const initial = { start: startDate, end: endDate };
      setRange(initial);
      setPreset(detectPreset(initial));
    }
  }, [open, startDate, endDate]);

  const fetchStats = useCallback(async (r: Range) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    const started = performance.now();
    try {
      const params: Record<string, string> = {};
      if (r.start) params.startDate = r.start.format("YYYY-MM-DD");
      if (r.end) params.endDate = r.end.format("YYYY-MM-DD");
      const res = await axiosInstance.get<SbcStatsResponse>(
        "/reports/sbc-stats",
        { params },
      );
      if (id !== requestId.current) return;
      setData(res.data);
      setLoadSeconds((performance.now() - started) / 1000);
    } catch (err: any) {
      if (id !== requestId.current) return;
      const status = err?.response?.status;
      setError(
        status === 403
          ? "Admins only: you are not authorized to view these statistics."
          : err?.response?.data?.message ||
              err?.message ||
              "Failed to load SBC stats.",
      );
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    // Custom with an open-ended start is still a valid query; skip only when
    // a custom range is inverted.
    if (range.start && range.end && range.end.isBefore(range.start, "day")) {
      setError("End date must be on or after the start date.");
      return;
    }
    fetchStats(range);
  }, [open, range, fetchStats]);

  const handlePreset = (_: unknown, value: Preset | null) => {
    if (!value) return;
    setPreset(value);
    if (value !== "custom") setRange(presetRange(value));
  };

  // Client-side column filter: respect the page's excludedEmails filter.
  const visibleUsers: SbcStatsUser[] = useMemo(() => {
    if (!data) return [];
    if (!users || users.length === 0) return data.users;
    const allowed = new Set(users.map((u) => String(u.userId)));
    return data.users.filter((u) => allowed.has(String(u.userId)));
  }, [data, users]);

  const sections: SectionDef[] = useMemo(() => {
    const extraTypes = Object.keys(data?.total?.referralTypes || {}).filter(
      (k) => !REFERRAL_TYPE_ORDER.includes(k),
    );
    const typeKeys = [...REFERRAL_TYPE_ORDER, ...extraTypes];
    return [
      {
        title: "One-on-One Support",
        rows: [
          { kind: "metric", label: "Consulting sessions", key: "consultingSessions" },
          { kind: "metric", label: "Companies served", key: "companiesServed" },
          { kind: "metric", label: "Contact hours", key: "contactHours", hours: true },
          { kind: "metric", label: "Prep hours", key: "prepHours", hours: true },
          { kind: "metric", label: "Travel hours", key: "travelHours", hours: true },
        ],
      },
      {
        title: "Case Pipeline",
        rows: [
          { kind: "metric", label: "Cases created", key: "caseCreated" },
          { kind: "metric", label: "Initial contacts", key: "initialContact" },
          { kind: "metric", label: "Business assessments", key: "businessAssessment" },
          { kind: "metric", label: "Cases closed", key: "caseClosed" },
        ],
      },
      {
        title: "Referrals",
        rows: [
          { kind: "metric", label: "To resource partners", key: "resourceReferrals" },
          { kind: "metric", label: "15-min B2B intros", key: "b2b15min" },
          { kind: "metric", label: "B2B consultations", key: "b2bProviderReferrals" },
          { kind: "metric", label: "Completed B2B", key: "b2bReferralsCompleted" },
        ],
      },
      {
        title: "Referral Types",
        rows: typeKeys.map((k) => ({ kind: "referralType", label: k, key: k })),
      },
      {
        title: "Company Demographics",
        rows: [
          { kind: "metric", label: "Minority-owned", key: "minorityOwned" },
          { kind: "metric", label: "Woman-owned", key: "womenOwned" },
          { kind: "metric", label: "Veteran-owned", key: "veteranOwned" },
        ],
      },
    ];
  }, [data]);

  const rangeLabel = (r: Range) =>
    `${r.start ? r.start.format("YYYY-MM-DD") : "all"}_${
      r.end ? r.end.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
    }`;

  // Build the table as rows of strings for copy/CSV.
  const buildGrid = (): string[][] => {
    const header = [
      "Metric",
      ...visibleUsers.map((u) => `${u.firstName} ${u.lastName}`.trim()),
      "Total",
    ];
    const grid: string[][] = [header];
    for (const section of sections) {
      grid.push([section.title]);
      for (const row of section.rows) {
        const hours = row.kind === "metric" && row.hours;
        grid.push([
          row.label,
          ...visibleUsers.map((u) =>
            hours ? valueOf(u.metrics, row).toFixed(1) : String(valueOf(u.metrics, row)),
          ),
          hours
            ? valueOf(data?.total, row).toFixed(1)
            : String(valueOf(data?.total, row)),
        ]);
      }
    }
    return grid;
  };

  const copyTable = async () => {
    const text = buildGrid()
      .map((r) => r.join("\t"))
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setSnack("Table copied to clipboard");
    } catch {
      setSnack("Could not copy to clipboard");
    }
  };

  const downloadCsv = () => {
    const csvEscape = (s: string) =>
      /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    const csv = buildGrid()
      .map((r) => r.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sbc-stats_${rangeLabel(range)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const numberCellSx = { textAlign: "right", whiteSpace: "nowrap" } as const;

  const renderNumber = (v: number, hours?: boolean, isMax?: boolean) => {
    if (v === 0) {
      return <span style={{ color: "#9e9e9e" }}>–</span>;
    }
    return (
      <span style={isMax ? { fontWeight: 700 } : undefined}>
        {formatValue(v, hours)}
      </span>
    );
  };

  return (
    <ThriveModal
      width={1280}
      open={open}
      setOpen={setOpen}
      contentComponent={
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              textAlign: "center",
              backgroundColor: MAGENTA,
              padding: "3px",
              margin: 0,
              color: "white",
              borderRadius: "5px 5px 0 0",
              marginBottom: "12px",
            }}
          >
            <Typography sx={{ display: "inline-block" }} variant="h4">
              SBC Stats
            </Typography>
            <Box
              sx={{ display: "inline", float: "right", cursor: "pointer" }}
              onClick={() => setOpen(false)}
            >
              <CloseIcon sx={{ fontSize: 40 }} />
            </Box>
          </Box>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ px: 2, pb: 1 }}
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={preset}
              onChange={handlePreset}
              sx={{
                "& .Mui-selected": {
                  backgroundColor: `${MAGENTA_TINT} !important`,
                  color: MAGENTA,
                  fontWeight: 700,
                },
              }}
            >
              <ToggleButton value="ytd">YTD</ToggleButton>
              <ToggleButton value="this_quarter">This quarter</ToggleButton>
              <ToggleButton value="last_quarter">Last quarter</ToggleButton>
              <ToggleButton value="all">All time</ToggleButton>
              <ToggleButton value="custom">Custom</ToggleButton>
            </ToggleButtonGroup>
            {preset === "custom" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={range.start}
                  onChange={(d) => setRange((r) => ({ ...r, start: d }))}
                  disableFuture
                  slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                  label="End Date"
                  value={range.end}
                  onChange={(d) => setRange((r) => ({ ...r, end: d }))}
                  shouldDisableDate={(d: Dayjs) =>
                    range.start ? d.isBefore(range.start, "day") : false
                  }
                  slotProps={{ textField: { size: "small" } }}
                />
              </LocalizationProvider>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {loading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} sx={{ color: MAGENTA }} />
                <Typography variant="caption" color="text.secondary">
                  Loading…
                </Typography>
              </Stack>
            ) : (
              loadSeconds !== null && (
                <Typography variant="caption" color="text.secondary">
                  loaded in {loadSeconds.toFixed(2)}s
                  {data?.range
                    ? ` · ${data.range.startDate || "all time"} → ${data.range.endDate}`
                    : ""}
                </Typography>
              )
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
              {error}
            </Alert>
          )}

          <TableContainer
            sx={{
              flexGrow: 1,
              overflow: "auto",
              px: 2,
              maxHeight: 480,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      minWidth: 200,
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      backgroundColor: "#fff",
                    }}
                  >
                    Metric
                  </TableCell>
                  {visibleUsers.map((u) => (
                    <TableCell
                      key={u.userId}
                      sx={{ ...numberCellSx, verticalAlign: "top" }}
                    >
                      <Box sx={{ fontWeight: 700 }}>
                        {u.firstName} {u.lastName}
                      </Box>
                      <Typography
                        component="span"
                        variant="caption"
                        onClick={() => onOpenDetail(u)}
                        sx={{
                          color: MAGENTA,
                          cursor: "pointer",
                          fontWeight: 600,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Detail Report
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...numberCellSx, fontWeight: 700 }}>
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!data && !loading && !error && (
                  <TableRow>
                    <TableCell colSpan={visibleUsers.length + 2}>
                      No data.
                    </TableCell>
                  </TableRow>
                )}
                {data &&
                  sections.map((section) => (
                    <React.Fragment key={section.title}>
                      <TableRow>
                        <TableCell
                          colSpan={visibleUsers.length + 2}
                          sx={{
                            backgroundColor: "#f0f0f0",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            fontSize: "0.75rem",
                            letterSpacing: 0.5,
                          }}
                        >
                          {section.title}
                        </TableCell>
                      </TableRow>
                      {section.rows.map((row) => {
                        const hours = row.kind === "metric" && row.hours;
                        const userValues = visibleUsers.map((u) =>
                          valueOf(u.metrics, row),
                        );
                        const max = Math.max(0, ...userValues);
                        const total = valueOf(data.total, row);
                        return (
                          <TableRow key={`${section.title}-${row.key}`} hover>
                            <TableCell
                              sx={{
                                position: "sticky",
                                left: 0,
                                backgroundColor: "#fff",
                                zIndex: 1,
                              }}
                            >
                              {row.label}
                            </TableCell>
                            {visibleUsers.map((u, i) => {
                              const isMax =
                                max > 0 && userValues[i] === max;
                              return (
                                <TableCell
                                  key={u.userId}
                                  sx={{
                                    ...numberCellSx,
                                    backgroundColor: isMax
                                      ? MAGENTA_TINT
                                      : undefined,
                                  }}
                                >
                                  {renderNumber(userValues[i], hours, isMax)}
                                </TableCell>
                              );
                            })}
                            <TableCell sx={{ ...numberCellSx, fontWeight: 700 }}>
                              {renderNumber(total, hours, true)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ p: 2, borderTop: "1px solid #eee" }}
          >
            <Button
              variant="outlined"
              onClick={copyTable}
              disabled={!data || loading}
              sx={{ color: MAGENTA, borderColor: MAGENTA }}
            >
              Copy table
            </Button>
            <Button
              variant="contained"
              onClick={downloadCsv}
              disabled={!data || loading}
              sx={{ backgroundColor: MAGENTA, "&:hover": { backgroundColor: "#7a184c" } }}
            >
              Download CSV
            </Button>
          </Stack>

          <Snackbar
            open={snack !== null}
            autoHideDuration={2500}
            onClose={() => setSnack(null)}
            message={snack}
          />
        </Box>
      }
    />
  );
};

export default SbcStatsModal;
